import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CartService, CartItem } from '../../services/cart.service';
import { ProductResponse, CategoryWithProducts } from '../../services/api.service';
import { LanguageService } from '../../services/language.service';
import { CurrencyService } from '../../services/currency.service';
import { TranslateService } from '@ngx-translate/core';
import { MenuTabBarItem } from '../../components/menu-tab-bar/menu-tab-bar.component';
import { fadeInOut, listAnimation, scaleInOut } from '../../shared/animations';
import { ProductSheetService } from '../../services/product-sheet.service';
import { AnalyticsService } from '../../services/analytics.service';

@Component({
    selector: 'app-category-detail',
    templateUrl: './category-detail.component.html',
    styleUrls: ['./category-detail.component.scss'],
    animations: [fadeInOut, listAnimation, scaleInOut],
    standalone: false
})
export class CategoryDetailComponent implements OnInit, OnDestroy {
  category: CategoryWithProducts | null = null;
  searchText: string = '';
  selectedTabId: string = '';
  currentLang: string = '';
  currencySymbol: string = '';
  cartItems: CartItem[] = [];
  clientData: any = null;

  categoryName: string = '';
  hasSubcategories: boolean = false;
  tabItems: MenuTabBarItem[] = [];
  allProducts: ProductResponse[] = [];
  flatProducts: ProductResponse[] = [];
  displayedProducts: ProductResponse[] = [];

  showUnavailableAlert: boolean = false;
  unavailableAlertMessage: string = '';
  private unavailableSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private cartService: CartService,
    private languageService: LanguageService,
    private currencyService: CurrencyService,
    private translate: TranslateService,
    private productSheet: ProductSheetService,
    private analytics: AnalyticsService
  ) {}

  ngOnInit(): void {
    const categoryId = Number(this.route.snapshot.paramMap.get('id'));
    const rawCategoriesStr = sessionStorage.getItem('rawCategories');

    if (rawCategoriesStr) {
      const rawCategories: CategoryWithProducts[] = JSON.parse(rawCategoriesStr);
      this.category = rawCategories.find(c => c.id === categoryId) || null;
    }

    if (!this.category) {
      this.router.navigate(['/menu']);
      return;
    }

    this.languageService.currentLang$.subscribe(lang => {
      this.currentLang = lang;
      this.rebuildCategoryData();
    });
    this.currencyService.currencySymbol$.subscribe(s => this.currencySymbol = s);
    this.cartService.cartItems$.subscribe(items => this.cartItems = items);

    this.unavailableSub = this.cartService.unavailableProduct$.subscribe(event => {
      if (event.from && event.to) {
        this.unavailableAlertMessage = this.translate.instant('menu.availability.availableFromTo', { from: event.from, to: event.to });
      } else {
        this.unavailableAlertMessage = this.translate.instant('menu.availability.notAvailableNow');
      }
      this.showUnavailableAlert = true;
    });

    const clientStr = sessionStorage.getItem('clientData');
    if (clientStr) {
      this.clientData = JSON.parse(clientStr);
    }

    this.rebuildCategoryData();

    if (this.tabItems.length > 0 && !this.selectedTabId) {
      this.selectedTabId = this.tabItems[0].id;
      this.updateDisplayedProducts();
    }
  }

  ngOnDestroy(): void {
    this.unavailableSub?.unsubscribe();
  }

  private rebuildCategoryData(): void {
    if (!this.category) return;

    this.categoryName = this.getLocalizedName(this.category);
    this.hasSubcategories = (this.category.subcategories ?? []).some(s => (s.products?.length ?? 0) > 0);
    this.flatProducts = this.category.products ?? [];

    let all = [...(this.category.products ?? [])];
    for (const sub of this.category.subcategories ?? []) {
      all = all.concat(sub.products ?? []);
    }
    this.allProducts = all;

    const items: MenuTabBarItem[] = [];
    if (this.category.products && this.category.products.length > 0) {
      items.push({ id: 'main', name: this.getLocalizedName(this.category) });
    }
    for (const sub of this.category.subcategories ?? []) {
      if ((sub.products?.length ?? 0) > 0) {
        items.push({ id: `sub-${sub.id ?? 0}`, name: this.getLocalizedName(sub) });
      }
    }
    this.tabItems = items;

    this.updateDisplayedProducts();
  }

  private updateDisplayedProducts(): void {
    if (!this.category) { this.displayedProducts = []; return; }
    if (this.selectedTabId === 'main') {
      this.displayedProducts = this.category.products ?? [];
      return;
    }
    for (const sub of this.category.subcategories ?? []) {
      if (`sub-${sub.id ?? 0}` === this.selectedTabId) {
        this.displayedProducts = sub.products ?? [];
        return;
      }
    }
    this.displayedProducts = [];
  }

  get filteredProducts(): ProductResponse[] {
    if (!this.searchText.trim()) return [];
    const q = this.searchText.toLowerCase();
    return this.allProducts.filter(p =>
      p.name.toLowerCase().includes(q) ||
      p.description?.toLowerCase().includes(q)
    );
  }

  onTabSelected(item: MenuTabBarItem): void {
    this.selectedTabId = item.id;
    this.updateDisplayedProducts();
  }

  goBack(): void {
    this.router.navigate(['/menu']);
  }

  onProductClick(productId: number): void {
    // Track the product open from the category route (parity with menu.onProductClick),
    // otherwise opens from a category page are missing from the funnel.
    const product = this.allProducts.find(p => p.id === productId);
    this.analytics.log('product_open', {
      product_id: productId.toString(),
      product_name: product ? product.name : 'Unknown Product',
      entry_screen: 'category'
    });
    this.productSheet.open(productId);
  }

  addToCart(event: Event, product: ProductResponse): void {
    event.stopPropagation();
    this.cartService.addToCart(product, 'category_detail');
  }

  updateQuantity(event: Event, product: ProductResponse, change: number): void {
    event.stopPropagation();
    const current = this.getItemQuantity(product.id);
    if (current + change <= 0) {
      this.cartService.removeFromCart(product.id, 'category_detail');
    } else {
      this.cartService.updateQuantity(product.id, current + change, 'category_detail');
    }
  }

  /**
   * The quantity of a dish across EVERY line it occupies, matching the menu screen.
   *
   * The old `cartItems.find(i => i.id === productId)` answered with whichever line happened to come
   * first, so a guest holding two cheese pizzas and one bacon pizza was told they had two. The tile
   * computes its own badge now, but this number still feeds the no-add-ons stepper below and would
   * be the wrong starting point the day a dish here gains add-ons.
   */
  getItemQuantity(productId: number): number {
    return this.cartService.getProductQuantity(productId);
  }

  canEnableOrdering(): boolean {
    const orderingType = sessionStorage.getItem('orderingType');
    // Match iOS isOrderingEnabled: is_ordering_active must be true, is_open_now/is_order_supported nil = OK
    const isOpenNow = this.clientData?.is_open_now !== false;
    const isOrderSupported = this.clientData?.is_order_supported !== false;
    if (orderingType === 'dine-in') {
      return this.clientData?.feature_flags?.ft_orders_from_table === true &&
        this.clientData?.is_ordering_active === true &&
        isOpenNow &&
        isOrderSupported;
    }
    if (orderingType === 'off-site' || orderingType === 'delivery') {
      return this.clientData?.feature_flags?.ft_orders_from_home === true &&
        this.clientData?.is_ordering_active === true &&
        isOpenNow &&
        isOrderSupported;
    }
    return false;
  }

  private getLocalizedName(cat: CategoryWithProducts): string {
    return this.languageService.localizeJsonMap(cat.description, cat.name || '');
  }
}
